/**
 * @file The while-open refresh trigger for Form Derived Values: ticks on the
 * shortest interval any entry of the form type configures and asks the server for a
 * fresh pass through `formDeriveRecomputeForm`. Consumer: `FormPageContent`, which
 * calls it once for the open form.
 * The timer lives on the client so a form nobody is looking at costs nothing: it
 * runs only while this form is open, only on a `draft` version — `formDeriveVersionTrigger`
 * reconciles after the state write lands, so a later state has had a pass on the
 * state it now carries and its numbers are binding — and skips a tick while the
 * tab is hidden. Several viewers tick independently and the reconciler's lock
 * coalesces them, so a refusal is normal operation and is never reported: `aborted`
 * means a pass already holds this version, and a background timer has no place to
 * put any other failure. The drawer's staleness line is where a person reads how
 * current the values are.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useSnapshot } from 'valtio';

import { formStore } from '@/store';
import { callFormDeriveRecomputeForm } from '@/services/callFormDeriveRecomputeForm';
import { createFormDeriveEngineConfig, FORM_DERIVE_REFRESH_MIN_MS } from '#types';

const MILLIS_PER_MINUTE = 60_000;

const useFormDeriveRefreshTimer = (): void => {
  const { formId, versionId } = useSnapshot(formStore.state);
  const formTypeSnap = useSnapshot(formStore.state.formType);
  const versionSnap = useSnapshot(formStore.state.formVersion);
  const formDerives = formTypeSnap.data?.formDerives;
  const formDeriveRev = formTypeSnap.data?.formDeriveRev;
  const versionStateId = versionSnap.data?.state;

  const config = useMemo(
    () => createFormDeriveEngineConfig({ formDerives, formDeriveRev }),
    [formDerives, formDeriveRev],
  );

  /**
   * One pass recomputes every derive, so the shortest configured interval is the
   * only one that has to be honored. The floor is the subsystem's own refresh
   * floor: a tick landing inside a running pass only sets `recomputeRequested`,
   * so a narrower window buys nothing observable and costs a full table read.
   */
  const intervalMs = useMemo(() => {
    const minutes = config.entries.flatMap((entry) => {
      const configured = entry.refresh?.whileOpenIntervalMinutes;
      return configured !== undefined && configured > 0 ? [configured] : [];
    });
    if (minutes.length === 0) return null;
    return Math.max(
      Math.min(...minutes) * MILLIS_PER_MINUTE,
      FORM_DERIVE_REFRESH_MIN_MS,
    );
  }, [config.entries]);

  /** A tick that finds the previous request still in flight skips its turn. */
  const requesting = useRef(false);

  useEffect(() => {
    if (intervalMs === null) return;
    if (formId === undefined || versionId === undefined) return;
    if (versionStateId !== 'draft') return;

    const tick = (): void => {
      if (requesting.current) return;
      if (document.visibilityState !== 'visible') return;
      requesting.current = true;
      void callFormDeriveRecomputeForm(formId, versionId)
        .catch(() => undefined)
        .finally(() => {
          requesting.current = false;
        });
    };

    const timer = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(timer);
  }, [formId, intervalMs, versionId, versionStateId]);
};

export { useFormDeriveRefreshTimer };
